import {
  BitkitAlert,
  BitkitButton,
  createBitkitToast,
  IconBlockCircle,
  IconCheck,
  IconLightbulb,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Card } from '@chakra-ui/react/card';
import { Separator } from '@chakra-ui/react/separator';
import { Text } from '@chakra-ui/react/text';
import { differenceInDays } from 'date-fns';
import { ReactNode, useMemo } from 'react';

import useMachineMigrationOpportunitiesOptIn, {
  SkipReason,
} from '@/client/BFF/App/MachineMigrationOpportunity/useMachineMigrationOpportunitiesOptIn';
import useMachineMigrationOpportunitiesOptOut from '@/client/BFF/App/MachineMigrationOpportunity/useMachineMigrationOpportunitiesOptOut';
import useMachineMigrationOpportunitiesShow from '@/client/BFF/App/MachineMigrationOpportunity/useMachineMigrationOpportunitiesShow';
import { getStandardErrorMessage } from '@/utils/errorResponseParser';

const skipReasonsTextMap = (workflow: string): Record<SkipReason, { title: string; description: ReactNode }> => ({
  bitrise_yml_in_repo: {
    title: 'Machine type switch skipped',
    description: (
      <>
        We couldn&apos;t switch the build machine for the Workflow{' '}
        <Text as="span" fontWeight="bold">
          {workflow}
        </Text>{' '}
        as the bitrise.yml is stored in your repository. Please perform the switch manually.
      </>
    ),
  },
  machine_type_mismatch: {
    title: 'Machine type switch skipped',
    description: (
      <>
        Switch was skipped because the build machine for the Workflow{' '}
        <Text as="span" fontWeight="bold">
          {workflow}
        </Text>{' '}
        has been changed since.
      </>
    ),
  },

  workflow_not_found: {
    title: 'Machine type switch skipped',
    description: (
      <>
        Switch was skipped because the workflow{' '}
        <Text as="span" fontWeight="bold">
          {workflow}
        </Text>{' '}
        was not found in your bitrise.yml.
      </>
    ),
  },
});

type MachineMigrationOpportunitySectionProps = { appSlug: string };

const MachineMigrationOpportunitySection = (props: MachineMigrationOpportunitySectionProps) => {
  const { data: machineMigrationOpportunity } = useMachineMigrationOpportunitiesShow(props.appSlug);

  const {
    error: optInError,
    mutate: optIn,
    isPending: isOptInPending,
    reset: resetOptIn,
    targetWorkflow: optInTargetWorkflow,
  } = useMachineMigrationOpportunitiesOptIn(props.appSlug, ({ skippedWithReason, workflow }) => {
    const { title, description, status } = (skippedWithReason && {
      ...skipReasonsTextMap(workflow)[skippedWithReason],
      status: 'info' as const,
    }) || {
      title: 'Machine type switched',
      description: (
        <>
          Build machine for the Workflow{' '}
          <Text as="span" fontWeight="bold">
            {workflow}
          </Text>{' '}
          has been successfully changed. You can change this any time.
        </>
      ),
      status: 'success' as const,
    };

    createBitkitToast({
      messageText: description,
      titleText: title,
      variant: status,
    });
  });

  const {
    error: optOutError,
    mutate: optOut,
    isPending: isOptOutPending,
    reset: resetOptOut,
    targetWorkflow: optOutTargetWorkflow,
  } = useMachineMigrationOpportunitiesOptOut(props.appSlug, ({ workflow }) => {
    createBitkitToast({
      messageText: (
        <>
          Build machine for the Workflow{' '}
          <Text as="span" fontWeight="bold">
            {workflow}
          </Text>{' '}
          won&apos;t be changed.
        </>
      ),
      titleText: 'Preference saved',
      variant: 'success',
    });
  });

  const daysUntilApplies = useMemo(() => {
    if (!machineMigrationOpportunity) {
      return;
    }
    return differenceInDays(new Date(machineMigrationOpportunity.migrationDate), new Date());
  }, [machineMigrationOpportunity]);

  const entries = useMemo(() => {
    if (!machineMigrationOpportunity) {
      return;
    }
    return machineMigrationOpportunity.triggeredWorkflows.filter((entry) => entry.status === null);
  }, [machineMigrationOpportunity]);

  if (!entries || daysUntilApplies === undefined) {
    return null;
  }

  const error = optInError || optOutError;

  return (
    <Box display="flex" flexDirection="column" gap={16}>
      <Text as="h2" textStyle="heading/h2">
        Optimization available ({entries.length})
      </Text>
      <Box display="flex" flexDir={{ base: 'column', tablet: 'row' }} flexWrap={{ tablet: 'wrap' }} gap={16}>
        {entries.length === 0 && <BitkitAlert variant="info" messageText="All optimization resolved." />}
        {entries.map((entry) => (
          <Card.Root
            asChild
            flexGrow={{ tablet: 1 }}
            key={entry.triggeredWorkflow}
            maxW={{ tablet: 600 }}
            minW={{ tablet: 360 }}
            w={{ tablet: '50%' }}
            paddingSize="sm"
          >
            <Card.Body display="flex" flexDir="column" gap={12}>
              <Box alignItems="flex-start" display="flex" gap={12}>
                <Box bg="status/info/bg-hover" borderRadius="8" padding="8">
                  <IconLightbulb color="color/blue/base" size="24" />
                </Box>
                <Box>
                  <Text fontWeight="600">Switch to a smaller machine</Text>
                  <Text color="text/secondary" textStyle="body/md/regular">
                    Same speed for less cost.
                  </Text>
                </Box>
              </Box>
              <Separator />
              {[
                { label: 'Workflow', value: entry.triggeredWorkflow },
                { label: 'Benefit', value: `Save $${entry.estimatedMonthlyCostChange}/month` },
                { label: 'Suggestion', value: `Switch to ${entry.suggestedMachineType}` },
                { label: 'Trade-off', value: 'No change in speed' },
              ].map(({ label, value }) => {
                return (
                  <Box key={label} display="flex" gap={12} justifyContent="space-between">
                    <Text textStyle="body/md/semibold">{label}</Text>
                    <Text textStyle="body/md/regular">{value}</Text>
                  </Box>
                );
              })}
              <Separator />
              <Box alignItems="center" display="flex" flexWrap="wrap" gap={12}>
                <Text color="text/secondary" textStyle="body/sm/regular">
                  Applies automatically{' '}
                  <Text as="span" color="status/critical/text">
                    {daysUntilApplies > 0 ? `in ${daysUntilApplies} days` : 'today'}.
                  </Text>
                </Text>
                <Box display="flex" flexWrap="wrap" gap={8} marginLeft="auto">
                  <BitkitButton
                    variant="danger-secondary"
                    state={isOptOutPending && optOutTargetWorkflow === entry.triggeredWorkflow ? 'loading' : undefined}
                    icon={IconBlockCircle}
                    marginInlineStart="auto"
                    onClick={() => {
                      resetOptIn();
                      optOut({ workflow: entry.triggeredWorkflow });
                    }}
                    size="sm"
                  >
                    Do not switch
                  </BitkitButton>
                  <BitkitButton
                    state={isOptInPending && optInTargetWorkflow === entry.triggeredWorkflow ? 'loading' : undefined}
                    icon={IconCheck}
                    marginInlineStart="auto"
                    onClick={() => {
                      resetOptOut();
                      optIn({ workflow: entry.triggeredWorkflow });
                    }}
                    size="sm"
                    variant="secondary"
                  >
                    Switch now
                  </BitkitButton>
                </Box>
              </Box>
            </Card.Body>
          </Card.Root>
        ))}
      </Box>
      {error && <BitkitAlert variant="critical" messageText={getStandardErrorMessage(error)} />}
    </Box>
  );
};

export default MachineMigrationOpportunitySection;
